import {
  clearGeneratorPreview,
  setGeneratorPreview,
} from 'src/stores/generator-preview'
import { baseApolloClient } from './client'
import {
  GetTaskByIdDocument,
  SubscribeGeneratorPreviewEventsDocument,
  SubscribePersonalEventsDocument,
  SubscribeWorkflowEventsDocument,
} from './generated/graphql'
import { createSubscriptionHook } from './subscriptionHooks'

export const usePersonalEventsSubscription = createSubscriptionHook(
  SubscribePersonalEventsDocument,
)

export const useWorkflowProgressSubscription = createSubscriptionHook(
  SubscribeWorkflowEventsDocument,
)

export const useGeneratorPreviewEventsSubscription = createSubscriptionHook(
  SubscribeGeneratorPreviewEventsDocument,
)

/**
 * Hook that subscribes to personal events and automatically updates Apollo cache
 * Also handles side effects like clearing preview images
 */
export const usePersonalEventsSync = () => {
  return usePersonalEventsSubscription({
    onData: data => {
      const events = data.personalEvents

      // Handle task updates - update cache
      if (events?.taskUpdated) {
        const taskId = events.taskUpdated.id
        baseApolloClient.cache.updateQuery(
          {
            query: GetTaskByIdDocument,
            variables: { id: taskId },
          },
          existing => {
            if (!existing) return existing
            return { ...existing, task: events.taskUpdated }
          },
        )

        // Clear preview when task fails/cancels
        // Do not clear on 'completed'; let UI use it as placeholder until final image loads
        if (
          events.taskUpdated.status &&
          ['failed', 'cancelled'].includes(events.taskUpdated.status)
        ) {
          clearGeneratorPreview(events.taskUpdated.id)
        }
      }

      // Handle new notifications - trigger refetch
      if (events?.newNotification) {
        baseApolloClient.refetchQueries({
          include: ['GetMyUnreadNotiCount'],
        })
      }
    },
  })
}

/**
 * Hook that subscribes to generator preview events
 */
export const useGeneratorPreviewSync = () => {
  return useGeneratorPreviewEventsSubscription({
    onData: data => {
      const preview = data.generatorPreviewEvents?.generationPreview
      if (preview?.taskId && preview.images && preview.images.length > 0) {
        setGeneratorPreview(preview.taskId, preview.images)
      }
    },
  })
}
